import React, { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    Search,
    RefreshCw,
    Package,
    ChevronDown,
    Truck,
    Save,
    X,
    Mail,
    User as UserIcon,
    MapPin,
    Euro,
    History,
    AlertTriangle,
    Settings as SettingsIcon,
    CheckSquare,
    Square,
    Printer,
    Send,
} from "lucide-react";
import { toast } from "sonner";
import {
    ORDER_STATUSES,
    ORDER_STATUS_LABELS,
    ORDER_STATUS_STYLES,
    formatDateTime,
    formatPrice,
    getPendingAlert,
    PENDING_ALERT_DAYS,
} from "../lib/orderStatus";

export default function AdminOrders({ apiAuth }) {
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);
    const [onlyOverdue, setOnlyOverdue] = useState(false);
    const [pendingDays, setPendingDays] = useState(PENDING_ALERT_DAYS);
    const [editingThreshold, setEditingThreshold] = useState(false);
    const [thresholdInput, setThresholdInput] = useState(String(PENDING_ALERT_DAYS));
    const [savingThreshold, setSavingThreshold] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    // Load admin-configurable settings (pending_alert_days)
    useEffect(() => {
        apiAuth("/admin/settings")
            .then((r) => {
                const v = parseInt(r.data?.pending_alert_days, 10);
                if (!Number.isNaN(v) && v > 0) {
                    setPendingDays(v);
                    setThresholdInput(String(v));
                }
            })
            .catch(() => {});
    }, [apiAuth]);

    const saveThreshold = async () => {
        const v = parseInt(thresholdInput, 10);
        if (Number.isNaN(v) || v < 1 || v > 60) {
            toast.error("Limiar deve estar entre 1 e 60 dias");
            return;
        }
        setSavingThreshold(true);
        try {
            const { data } = await apiAuth("/admin/settings", {
                method: "PUT",
                data: { pending_alert_days: v },
            });
            setPendingDays(data.pending_alert_days);
            setThresholdInput(String(data.pending_alert_days));
            setEditingThreshold(false);
            toast.success("Limiar atualizado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro a guardar");
        } finally {
            setSavingThreshold(false);
        }
    };

    const overdueCount = useMemo(
        () => orders.filter((o) => getPendingAlert(o, pendingDays)).length,
        [orders, pendingDays],
    );

    const visibleOrders = useMemo(
        () =>
            onlyOverdue
                ? orders.filter((o) => getPendingAlert(o, pendingDays))
                : orders,
        [orders, onlyOverdue, pendingDays],
    );

    const load = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (filterStatus !== "all") params.set("status", filterStatus);
            if (search.trim()) params.set("q", search.trim());
            const qs = params.toString() ? `?${params.toString()}` : "";
            const [ordersRes, statsRes] = await Promise.all([
                apiAuth(`/admin/orders${qs}`),
                apiAuth("/admin/orders-stats"),
            ]);
            setOrders(ordersRes.data || []);
            setStats(statsRes.data || null);
        } catch {
            toast.error("Erro ao carregar encomendas");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus]);

    const submitSearch = (e) => {
        e.preventDefault();
        load();
    };

    const handleUpdate = async (orderId, patch) => {
        try {
            const { data } = await apiAuth(`/admin/orders/${orderId}`, {
                method: "PATCH",
                data: patch,
            });
            setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
            toast.success("Encomenda atualizada");
            // refresh stats
            apiAuth("/admin/orders-stats").then((r) => setStats(r.data)).catch(() => {});
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao atualizar");
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = (rows) => {
        setSelectedIds((prev) => {
            if (prev.size >= rows.length) return new Set();
            return new Set(rows.map((r) => r.id));
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const bulkUpdate = async (newStatus) => {
        if (selectedIds.size === 0) return;
        const label = ORDER_STATUS_LABELS[newStatus] || newStatus;
        if (!window.confirm(`Marcar ${selectedIds.size} encomenda(s) como "${label}"?`)) return;
        setBulkBusy(true);
        try {
            const { data } = await apiAuth("/admin/orders/bulk-update", {
                method: "POST",
                data: {
                    order_ids: Array.from(selectedIds),
                    order_status: newStatus,
                },
            });
            toast.success(
                `${data.updated} encomenda(s) atualizada(s)${
                    data.skipped?.length ? ` · ${data.skipped.length} ignoradas` : ""
                }`,
            );
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro no bulk update");
        } finally {
            setBulkBusy(false);
        }
    };

    const printPickingList = () => {
        const selected = orders.filter((o) => selectedIds.has(o.id));
        if (selected.length === 0) return;
        // Aggregate items across all selected orders
        const agg = {};
        for (const o of selected) {
            for (const it of o.items_snapshot || []) {
                const pid = it.product_id || "unknown";
                if (!agg[pid]) {
                    agg[pid] = { name: it.name || pid, quantity: 0, orders: 0 };
                }
                agg[pid].quantity += Number(it.quantity || 0);
                agg[pid].orders += 1;
            }
        }
        const rows = Object.entries(agg)
            .map(
                ([pid, v]) => `
                <tr>
                    <td>${escapeHtml(v.name)}</td>
                    <td style="text-align:right; font-weight:700;">${v.quantity}</td>
                    <td style="text-align:right; color:#666;">${v.orders} enc.</td>
                </tr>`,
            )
            .join("");
        const orderList = selected
            .map(
                (o) => `
                <li>
                    <strong>#${o.order_number || o.id.slice(0, 8)}</strong> —
                    ${escapeHtml(o.customer_name || o.customer_email || "Cliente")}
                    <span style="color:#666; margin-left: 8px;">${formatPrice(o.amount || 0)}</span>
                </li>`,
            )
            .join("");

        const html = `<!doctype html>
<html><head>
<meta charset="utf-8">
<title>Picking List — Sabor Líquido</title>
<style>
  body { font-family: system-ui, -apple-system, sans-serif; padding: 32px; color: #1C221F; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
  h2 { font-size: 15px; margin: 24px 0 8px; text-transform: uppercase; letter-spacing: 0.1em; color: #7A8B76; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th, td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: left; font-size: 14px; }
  th { background: #F9F6F0; text-transform: uppercase; font-size: 11px; letter-spacing: 0.1em; color: #666; }
  ul { padding-left: 20px; font-size: 13px; line-height: 1.7; }
  .stamp { display: inline-block; background: #7A8B76; color: white; padding: 4px 10px; border-radius: 999px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700; }
</style>
</head><body>
  <h1>Picking List <span class="stamp">${selected.length} enc.</span></h1>
  <div class="meta">Gerado em ${new Date().toLocaleString("pt-PT")}</div>

  <h2>Total a preparar</h2>
  <table>
    <thead><tr><th>Produto</th><th style="text-align:right;">Unidades</th><th style="text-align:right;">Nº enc.</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <h2>Encomendas incluídas</h2>
  <ul>${orderList}</ul>

  <script>window.addEventListener("load", () => setTimeout(() => window.print(), 400));</script>
</body></html>`;
        const w = window.open("", "_blank", "width=900,height=1000");
        if (!w) {
            toast.error("Popup bloqueado — permite popups para imprimir");
            return;
        }
        w.document.write(html);
        w.document.close();
    };

    return (
        <div className="space-y-6" data-testid="admin-orders-panel">
            {/* Stats */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Total encomendas"
                    value={stats?.total ?? "—"}
                    bg="bg-white"
                    accent="text-brand-green"
                    icon={Package}
                    testid="orders-stat-total"
                />
                <StatCard
                    label="Receita total"
                    value={stats ? formatPrice(stats.revenue) : "—"}
                    bg="bg-brand-green"
                    accent="text-white"
                    icon={Euro}
                    white
                    testid="orders-stat-revenue"
                />
                <StatCard
                    label="A enviar"
                    value={
                        (stats?.by_status?.paga || 0) + (stats?.by_status?.em_preparacao || 0)
                    }
                    bg="bg-brand-orange-soft"
                    accent="text-brand-orange"
                    icon={Package}
                    testid="orders-stat-toship"
                />
                <StatCard
                    label="Em trânsito"
                    value={
                        (stats?.by_status?.enviada || 0) + (stats?.by_status?.em_transito || 0)
                    }
                    bg="bg-brand-ink"
                    accent="text-white"
                    icon={Truck}
                    white
                    testid="orders-stat-transit"
                />
            </section>

            {/* Overdue alert banner */}
            {overdueCount > 0 && (
                <div
                    data-testid="orders-overdue-banner"
                    className={`w-full rounded-2xl ring-1 px-5 py-4 flex items-center gap-3 transition-all ${
                        onlyOverdue
                            ? "bg-red-50 ring-red-300 shadow-pop"
                            : "bg-amber-50 ring-amber-200"
                    }`}
                >
                    <button
                        type="button"
                        onClick={() => setOnlyOverdue((v) => !v)}
                        data-testid="orders-overdue-toggle"
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                        <span className="h-10 w-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
                            <AlertTriangle className="h-5 w-5" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-medium text-brand-ink">
                                {overdueCount}{" "}
                                {overdueCount === 1 ? "encomenda" : "encomendas"} pendentes há
                                mais de {pendingDays} dias
                            </div>
                            <div className="text-xs text-brand-ink-soft">
                                {onlyOverdue
                                    ? "A mostrar apenas atrasadas. Clica para ver tudo."
                                    : "Clica para filtrar e tratar destas encomendas primeiro."}
                            </div>
                        </div>
                        <span
                            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-3 py-1 ${
                                onlyOverdue
                                    ? "bg-red-600 text-white"
                                    : "bg-amber-200 text-amber-900"
                            }`}
                        >
                            {onlyOverdue ? "ativo" : "filtrar"}
                        </span>
                    </button>
                    <ThresholdEditor
                        editing={editingThreshold}
                        setEditing={setEditingThreshold}
                        value={thresholdInput}
                        setValue={setThresholdInput}
                        onSave={saveThreshold}
                        saving={savingThreshold}
                        currentDays={pendingDays}
                    />
                </div>
            )}
            {overdueCount === 0 && (
                <div className="flex items-center justify-end">
                    <ThresholdEditor
                        editing={editingThreshold}
                        setEditing={setEditingThreshold}
                        value={thresholdInput}
                        setValue={setThresholdInput}
                        onSave={saveThreshold}
                        saving={savingThreshold}
                        currentDays={pendingDays}
                        compact
                    />
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <form onSubmit={submitSearch} className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-ink-soft" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar nome, email, nº..."
                            data-testid="admin-orders-search"
                            className="rounded-full bg-white border-2 border-brand-green/15 pl-9 pr-4 py-2 text-xs font-semibold text-brand-ink focus:outline-none focus:border-brand-green w-64"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid="admin-orders-search-submit"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-white px-4 py-2 text-xs font-semibold"
                    >
                        Procurar
                    </button>
                </form>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="admin-orders-status-filter"
                    className="rounded-full bg-white border-2 border-brand-green/15 px-4 py-2 text-xs font-semibold text-brand-ink focus:outline-none focus:border-brand-green"
                >
                    <option value="all">Todos os estados</option>
                    {ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {ORDER_STATUS_LABELS[s]}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-orders-refresh"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </div>

            {/* Bulk actions toolbar */}
            {selectedIds.size > 0 && (
                <div
                    className="rounded-2xl bg-brand-ink text-white p-4 flex items-center gap-3 flex-wrap shadow-pop"
                    data-testid="bulk-toolbar"
                >
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-medium text-lg">
                            {selectedIds.size} encomenda{selectedIds.size > 1 ? "s" : ""} selecionada{selectedIds.size > 1 ? "s" : ""}
                        </div>
                        <div className="text-xs text-white/70">Aplique uma ação em bloco</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => bulkUpdate("enviada")}
                        disabled={bulkBusy}
                        data-testid="bulk-mark-sent"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                    >
                        <Send className="h-3.5 w-3.5" /> Marcar como enviadas
                    </button>
                    <button
                        type="button"
                        onClick={() => bulkUpdate("em_preparacao")}
                        disabled={bulkBusy}
                        data-testid="bulk-mark-preparing"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange hover:bg-brand-orange/90 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                    >
                        <Package className="h-3.5 w-3.5" /> Em preparação
                    </button>
                    <button
                        type="button"
                        onClick={printPickingList}
                        data-testid="bulk-print-picking"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white text-brand-ink px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-brand-cream"
                    >
                        <Printer className="h-3.5 w-3.5" /> Picking list
                    </button>
                    <button
                        type="button"
                        onClick={clearSelection}
                        data-testid="bulk-cancel"
                        className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-xs font-semibold"
                    >
                        <X className="h-3.5 w-3.5" /> Limpar
                    </button>
                </div>
            )}

            {/* List */}
            {busy ? (
                <div className="p-16 flex justify-center bg-white rounded-2xl shadow-soft">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                </div>
            ) : visibleOrders.length === 0 ? (
                <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-16 text-center">
                    <Package className="h-10 w-10 text-brand-green/40 mx-auto" />
                    <p className="mt-4 text-sm text-brand-ink-soft">
                        {onlyOverdue
                            ? "Sem encomendas pendentes acima do limite."
                            : "Sem encomendas com estes filtros."}
                    </p>
                </div>
            ) : (
                <>
                    {/* Select all */}
                    <div className="flex items-center gap-2 text-xs text-brand-ink-soft">
                        <button
                            type="button"
                            onClick={() => toggleSelectAll(visibleOrders)}
                            data-testid="bulk-select-all"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-cream px-3 py-1.5 font-semibold text-brand-ink"
                        >
                            {selectedIds.size >= visibleOrders.length && visibleOrders.length > 0 ? (
                                <CheckSquare className="h-3.5 w-3.5 text-brand-green" />
                            ) : (
                                <Square className="h-3.5 w-3.5" />
                            )}
                            {selectedIds.size >= visibleOrders.length && visibleOrders.length > 0
                                ? "Desmarcar todas"
                                : `Selecionar ${visibleOrders.length}`}
                        </button>
                    </div>
                    <ul className="space-y-3">
                        {visibleOrders.map((o) => (
                            <OrderRow
                                key={o.id}
                                order={o}
                                isOpen={openId === o.id}
                                onToggle={() => setOpenId((prev) => (prev === o.id ? null : o.id))}
                                onUpdate={(patch) => handleUpdate(o.id, patch)}
                                pendingDays={pendingDays}
                                selected={selectedIds.has(o.id)}
                                onSelect={() => toggleSelect(o.id)}
                            />
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

function OrderRow({ order, isOpen, onToggle, onUpdate, pendingDays, selected, onSelect }) {
    const alert = getPendingAlert(order, pendingDays);
    return (
        <li
            data-testid={`admin-order-row-${order.id}`}
            className={`rounded-2xl bg-white shadow-soft overflow-hidden ${
                selected
                    ? "ring-2 ring-brand-green"
                    : alert
                        ? alert.level === "danger"
                            ? "ring-2 ring-red-400"
                            : "ring-2 ring-amber-300"
                        : "ring-1 ring-brand-green/10"
            }`}
        >
            <div className="flex items-stretch">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect?.();
                    }}
                    data-testid={`admin-order-select-${order.id}`}
                    className={`px-4 flex items-center justify-center border-r border-brand-green/10 hover:bg-brand-cream transition-colors ${
                        selected ? "bg-brand-green-soft" : ""
                    }`}
                    aria-label="Selecionar"
                >
                    {selected ? (
                        <CheckSquare className="h-5 w-5 text-brand-green" />
                    ) : (
                        <Square className="h-5 w-5 text-brand-ink-soft" />
                    )}
                </button>
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-1 min-w-0 px-5 py-4 flex items-center justify-between gap-4 hover:bg-brand-cream/40 transition-colors text-left"
                    data-testid={`admin-order-toggle-${order.id}`}
                >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-medium text-brand-ink">
                            #{order.order_number || order.id.slice(0, 8)}
                        </span>
                        <span
                            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2.5 py-1 ${
                                ORDER_STATUS_STYLES[order.order_status] ||
                                "bg-brand-cream text-brand-ink"
                            }`}
                        >
                            {ORDER_STATUS_LABELS[order.order_status] || order.order_status}
                        </span>
                        {alert && (
                            <span
                                data-testid={`admin-order-overdue-${order.id}`}
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                                    alert.level === "danger"
                                        ? "bg-red-600 text-white"
                                        : "bg-amber-200 text-amber-900"
                                }`}
                                title={`Pendente há ${alert.days} dias`}
                            >
                                <AlertTriangle className="h-3 w-3" />
                                {alert.days}d sem expedir
                            </span>
                        )}
                        {order.tracking_number && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-brand-green-soft text-brand-green px-2 py-0.5">
                                <Truck className="h-3 w-3" />
                                {order.carrier || "Envio"}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-brand-ink-soft">
                        {order.customer_name && (
                            <span className="inline-flex items-center gap-1">
                                <UserIcon className="h-3 w-3" />
                                {order.customer_name}
                            </span>
                        )}
                        {order.customer_email && (
                            <span className="inline-flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {order.customer_email}
                            </span>
                        )}
                        <span>{formatDateTime(order.created_at)}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-display font-medium text-lg text-brand-ink">
                        {formatPrice(order.amount, order.currency)}
                    </div>
                    <ChevronDown
                        className={`h-4 w-4 text-brand-ink-soft inline-block transition-transform ${
                            isOpen ? "rotate-180" : ""
                        }`}
                    />
                </div>
            </button>
            </div>

            {isOpen && <OrderDetailEditor order={order} onUpdate={onUpdate} />}
        </li>
    );
}

function OrderDetailEditor({ order, onUpdate }) {
    const [status, setStatus] = useState(order.order_status || "paga");
    const [tracking, setTracking] = useState(order.tracking_number || "");
    const [carrier, setCarrier] = useState(order.carrier || "");
    const [notes, setNotes] = useState(order.admin_notes || "");
    const [statusNote, setStatusNote] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const patch = {};
        if (status !== order.order_status) {
            patch.order_status = status;
            if (statusNote.trim()) patch.status_note = statusNote.trim();
        }
        if (tracking !== (order.tracking_number || "")) patch.tracking_number = tracking || null;
        if (carrier !== (order.carrier || "")) patch.carrier = carrier || null;
        if (notes !== (order.admin_notes || "")) patch.admin_notes = notes || null;

        if (Object.keys(patch).length === 0) {
            toast.info("Sem alterações");
            setSaving(false);
            return;
        }
        await onUpdate(patch);
        setStatusNote("");
        setSaving(false);
    };

    return (
        <div className="border-t border-brand-green/10 bg-brand-cream/40 px-5 py-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft">
                    Artigos
                </div>
                <ul className="mt-2 divide-y divide-brand-green/10 bg-white rounded-2xl p-4 shadow-soft">
                    {(order.items_snapshot?.length ? order.items_snapshot : order.items || []).map(
                        (it, i) => (
                            <li key={i} className="py-2 flex items-center justify-between text-sm">
                                <span className="text-brand-ink">
                                    {it.name || it.product_id}{" "}
                                    <span className="text-brand-ink-soft">x{it.quantity}</span>
                                </span>
                                {it.subtotal != null && (
                                    <span className="font-semibold text-brand-ink">
                                        {formatPrice(it.subtotal, order.currency)}
                                    </span>
                                )}
                            </li>
                        ),
                    )}
                </ul>

                {order.shipping_address && (
                    <div className="mt-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> Envio
                        </div>
                        <div className="mt-2 bg-white rounded-2xl p-4 shadow-soft text-sm text-brand-ink">
                            {order.shipping_address.full_name && (
                                <div className="font-semibold">{order.shipping_address.full_name}</div>
                            )}
                            <div>{order.shipping_address.line1}</div>
                            {order.shipping_address.line2 && <div>{order.shipping_address.line2}</div>}
                            <div>
                                {order.shipping_address.postal_code} {order.shipping_address.city}
                            </div>
                            <div>{order.shipping_address.country}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Editor */}
            <div className="lg:col-span-1 space-y-3">
                <EditLabel label="Estado da encomenda">
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        data-testid={`admin-order-status-select-${order.id}`}
                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                    >
                        {ORDER_STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {ORDER_STATUS_LABELS[s]}
                            </option>
                        ))}
                    </select>
                </EditLabel>
                <EditLabel label="Nota da mudança de estado (opcional)">
                    <input
                        type="text"
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        data-testid={`admin-order-status-note-${order.id}`}
                        placeholder="Ex: pagamento confirmado, entregue ao cliente..."
                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                    />
                </EditLabel>
                <div className="grid grid-cols-2 gap-3">
                    <EditLabel label="Transportadora">
                        <input
                            type="text"
                            value={carrier}
                            onChange={(e) => setCarrier(e.target.value)}
                            data-testid={`admin-order-carrier-${order.id}`}
                            placeholder="CTT, DHL..."
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                        />
                    </EditLabel>
                    <EditLabel label="Nº tracking">
                        <input
                            type="text"
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            data-testid={`admin-order-tracking-${order.id}`}
                            placeholder="TR1234567"
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                        />
                    </EditLabel>
                </div>
                <EditLabel label="Notas internas">
                    <textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid={`admin-order-notes-${order.id}`}
                        placeholder="Notas só visíveis ao admin..."
                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink focus:outline-none focus:border-brand-green resize-none"
                    />
                </EditLabel>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid={`admin-order-save-${order.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-5 py-2.5 text-xs font-semibold transition-all disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        Guardar
                    </button>
                </div>
            </div>

            {/* History */}
            <div className="lg:col-span-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft flex items-center gap-1">
                    <History className="h-3 w-3" /> Histórico de estados
                </div>
                <ol className="mt-2 bg-white rounded-2xl p-4 shadow-soft space-y-3">
                    {(order.status_history || []).map((h, i) => (
                        <li key={i} className="text-xs">
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                                        ORDER_STATUS_STYLES[h.status] || "bg-brand-cream text-brand-ink"
                                    }`}
                                >
                                    {ORDER_STATUS_LABELS[h.status] || h.status}
                                </span>
                                <span className="text-brand-ink-soft">{formatDateTime(h.at)}</span>
                            </div>
                            {h.note && (
                                <div className="mt-1 text-brand-ink-soft pl-1">{h.note}</div>
                            )}
                            {h.by && (
                                <div className="text-[10px] text-brand-ink-soft/70 pl-1">por {h.by}</div>
                            )}
                        </li>
                    ))}
                    {(!order.status_history || order.status_history.length === 0) && (
                        <li className="text-xs text-brand-ink-soft">Sem histórico.</li>
                    )}
                </ol>
            </div>
        </div>
    );
}

const EditLabel = ({ label, children }) => (
    <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft mb-1.5">
            {label}
        </div>
        {children}
    </div>
);

const StatCard = ({ icon: Icon, label, value, accent, bg, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-5 md:p-6 shadow-soft ring-1 ring-brand-green/5`}
    >
        <Icon className={`h-5 w-5 ${accent}`} />
        <div
            className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${
                white ? "text-white/80" : "text-brand-ink-soft"
            }`}
        >
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-3xl md:text-4xl ${
                white ? "text-white" : "text-brand-ink"
            }`}
        >
            {value}
        </div>
    </div>
);


function ThresholdEditor({
    editing,
    setEditing,
    value,
    setValue,
    onSave,
    saving,
    currentDays,
    compact = false,
}) {
    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid="orders-threshold-edit"
                className={`inline-flex items-center gap-1.5 rounded-full bg-white/80 hover:bg-white text-brand-ink-soft hover:text-brand-ink px-3 py-1.5 text-[11px] font-semibold transition-colors shrink-0 ${
                    compact ? "" : "ring-1 ring-brand-green/15"
                }`}
                title="Configurar limiar de alerta"
            >
                <SettingsIcon className="h-3 w-3" />
                Limiar: {currentDays}d
            </button>
        );
    }
    return (
        <div
            className="inline-flex items-center gap-1.5 bg-white rounded-full px-2 py-1 ring-1 ring-brand-green/15 shrink-0"
            data-testid="orders-threshold-editor"
        >
            <span className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft pl-1">
                Limiar
            </span>
            <input
                type="number"
                min="1"
                max="60"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="orders-threshold-input"
                className="w-14 rounded-full bg-brand-cream px-2 py-1 text-xs font-semibold text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-green"
            />
            <span className="text-[10px] text-brand-ink-soft">dias</span>
            <button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="orders-threshold-save"
                className="inline-flex items-center gap-1 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-2.5 py-1 text-[11px] font-semibold disabled:opacity-60"
            >
                {saving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                    <Save className="h-3 w-3" />
                )}
                Guardar
            </button>
            <button
                type="button"
                onClick={() => setEditing(false)}
                data-testid="orders-threshold-cancel"
                className="h-6 w-6 rounded-full hover:bg-brand-cream flex items-center justify-center text-brand-ink-soft"
                aria-label="Cancelar"
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
}

function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

